(() => {
  const STATUS_LABELS = {
    received: 'Bestelling ontvangen',
    processing: 'Wordt klaargemaakt',
    shipped: 'Verzonden',
    in_transit: 'Onderweg',
    available_for_pickup: 'Klaar om af te halen',
    out_for_delivery: 'Wordt vandaag bezorgd',
    delivered: 'Bezorgd',
    delayed: 'Vertraagd',
    issue: 'Bezorgprobleem',
    unknown: 'Status wordt bijgewerkt',
  };

  const STATUS_STEPS = {
    received: 0,
    processing: 1,
    shipped: 2,
    in_transit: 2,
    available_for_pickup: 2,
    out_for_delivery: 2,
    delivered: 3,
    delayed: 2,
    issue: 2,
    unknown: 1,
  };

  const ETA_NOTES = {
    '17track': 'Schatting van de vervoerder. De bezorgdatum kan nog veranderen.',
    shopify: 'Schatting uit de verzendgegevens van je bestelling.',
    historical_status: 'Schatting op basis van vergelijkbare zendingen uit de afgelopen 90 dagen.',
    dpw_estimate: 'Schatting op basis van onze normale verwerking en werkdagen.',
    combined: 'Je bestelling bestaat uit meerdere pakketten. De bezorgmomenten kunnen verschillen.',
  };

  function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  function textElement(tagName, className, value) {
    const element = document.createElement(tagName);
    element.className = className;
    element.textContent = value;
    return element;
  }

  function dateValue(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(value)) return null;
    const parsed = new Date(`${value.slice(0, 10)}T12:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function formatDate(value, options = {}) {
    const parsed = dateValue(value);
    if (!parsed) return null;
    return new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', {
      day: 'numeric',
      month: 'long',
      ...options,
    }).format(parsed);
  }

  function formatDateTime(value) {
    if (typeof value !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Amsterdam',
    }).format(parsed);
  }

  function deliveredDateValue(value) {
    if (typeof value !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return dateValue(value);
    const timestamp = new Date(value);
    return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  }

  function formatDeliveredDay(value) {
    const parsed = deliveredDateValue(value);
    if (!parsed) return null;
    return new Intl.DateTimeFormat(document.documentElement.lang || 'nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'Europe/Amsterdam',
    }).format(parsed);
  }

  function formatDeliveredDateTime(value) {
    const parsed = deliveredDateValue(value);
    if (!parsed) return null;
    const locale = document.documentElement.lang || 'nl-NL';
    const date = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      timeZone: 'Europe/Amsterdam',
    }).format(parsed);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return date;
    const time = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Amsterdam',
    }).format(parsed);
    return `${date} om ${time}`;
  }

  function formatEta(eta) {
    if (!isObject(eta)) return null;
    const from = formatDate(eta.from, { weekday: 'long' });
    const to = formatDate(eta.to, { weekday: 'long' });
    if (!from || !to) return null;
    return eta.from === eta.to ? from : `${from} – ${to}`;
  }

  class FinchOrderTracking extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-fot-form]');
      this.orderInput = this.querySelector('[data-fot-order-number]');
      this.postalInput = this.querySelector('[data-fot-postal-code]');
      this.submitButton = this.querySelector('[data-fot-submit]');
      this.feedback = this.querySelector('[data-fot-feedback]');
      this.result = this.querySelector('[data-fot-result]');
      this.resultLabel = this.querySelector('[data-fot-result-label]');
      this.eta = this.querySelector('[data-fot-eta]');
      this.etaNote = this.querySelector('[data-fot-eta-note]');
      this.overallStatus = this.querySelector('[data-fot-overall-status]');
      this.notice = this.querySelector('[data-fot-notice]');
      this.packagesWrap = this.querySelector('[data-fot-packages-wrap]');
      this.packagesHeading = this.querySelector('[data-fot-packages-heading]');
      this.packages = this.querySelector('[data-fot-packages]');
      this.timelineSteps = Array.from(this.querySelectorAll('[data-fot-step]'));

      if (!this.form || !this.orderInput || !this.postalInput || !this.submitButton) return;
      this.orderInput.addEventListener('blur', () => this.validateField(this.orderInput, 'Vul je bestelnummer in.'));
      this.postalInput.addEventListener('blur', () => this.validateField(this.postalInput, 'Vul een geldige postcode in.'));
      this.orderInput.addEventListener('input', () => this.clearFieldError(this.orderInput));
      this.postalInput.addEventListener('input', () => this.clearFieldError(this.postalInput));
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    clearFieldError(input) {
      input.setCustomValidity('');
      input.removeAttribute('aria-invalid');
    }

    validateField(input, message) {
      input.setCustomValidity('');
      if (!input.validity.valid) input.setCustomValidity(message);
      input.toggleAttribute('aria-invalid', !input.validity.valid);
      return input.validity.valid;
    }

    validateForm() {
      const orderValid = this.validateField(this.orderInput, 'Vul je bestelnummer in.');
      const postalValid = this.validateField(this.postalInput, 'Vul een geldige postcode in.');
      if (orderValid && postalValid) return true;
      this.form.reportValidity();
      const invalid = this.form.querySelector(':invalid');
      invalid?.focus();
      return false;
    }

    endpoint() {
      const value = this.dataset.endpoint?.trim();
      if (!value) return null;
      try {
        const url = new URL(value, window.location.origin);
        const local = ['localhost', '127.0.0.1'].includes(url.hostname);
        return url.protocol === 'https:' || local ? url.toString() : null;
      } catch (_error) {
        return null;
      }
    }

    setLoading(loading) {
      this.submitButton.disabled = loading;
      this.submitButton.toggleAttribute('aria-busy', loading);
      this.form.toggleAttribute('aria-busy', loading);
    }

    clearMessages() {
      this.feedback.hidden = true;
      this.feedback.textContent = '';
      this.result.hidden = true;
    }

    showError(message) {
      this.result.hidden = true;
      this.feedback.textContent = message;
      this.feedback.hidden = false;
      this.feedback.focus({ preventScroll: true });
      this.feedback.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    errorMessage(code) {
      if (code === 'no_match') return this.dataset.noMatchMessage;
      if (code === 'rate_limited') return this.dataset.rateLimitMessage;
      return this.dataset.technicalMessage;
    }

    async submit(event) {
      event.preventDefault();
      if (!this.validateForm()) return;
      const endpoint = this.endpoint();
      if (!endpoint) {
        this.showError(this.dataset.technicalMessage);
        this.trackOutcome('configuration_error');
        return;
      }

      this.clearMessages();
      this.setLoading(true);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20_000);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orderNumber: this.orderInput.value,
            postalCode: this.postalInput.value,
          }),
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok || !isObject(payload) || payload.ok !== true) {
          const code = isObject(payload) && typeof payload.code === 'string' ? payload.code : 'technical_error';
          this.showError(this.errorMessage(code));
          this.trackOutcome(code);
          return;
        }

        this.renderResult(payload);
        this.trackOutcome('success');
      } catch (error) {
        const outcome = error?.name === 'AbortError' ? 'timeout' : 'technical_error';
        this.showError(this.dataset.technicalMessage);
        this.trackOutcome(outcome);
      } finally {
        window.clearTimeout(timeout);
        this.setLoading(false);
      }
    }

    renderResult(payload) {
      const status = typeof payload.overallStatus === 'string' && STATUS_LABELS[payload.overallStatus]
        ? payload.overallStatus
        : 'unknown';
      if (status === 'delivered') {
        const deliveredDate = formatDeliveredDay(payload.deliveredAt);
        this.resultLabel.textContent = 'Bezorgstatus';
        this.eta.textContent = deliveredDate ? `Bezorgd op ${deliveredDate}` : 'Je bestelling is bezorgd';
        this.etaNote.textContent = 'Volgens de vervoerder is je bestelling bezorgd.';
      } else {
        const etaText = formatEta(payload.eta);
        this.resultLabel.textContent = 'Verwachte bezorging';
        this.eta.textContent = etaText || 'Nog geen bezorgdatum beschikbaar';
        this.etaNote.textContent = etaText
          ? ETA_NOTES[payload.eta?.source] || 'Dit is een schatting en kan nog veranderen.'
          : 'Zodra de vervoerder een betrouwbare bezorgdatum deelt, zie je die hier.';
      }
      this.overallStatus.textContent = STATUS_LABELS[status];
      this.overallStatus.dataset.status = status;
      this.renderTimeline(status);
      this.renderNotice(payload, status);
      this.renderPackages(Array.isArray(payload.packages) ? payload.packages : []);
      this.result.hidden = false;
      this.result.focus({ preventScroll: true });
      this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderTimeline(status) {
      const activeStep = STATUS_STEPS[status] ?? 1;
      this.timelineSteps.forEach((step, index) => {
        const complete = index < activeStep || status === 'delivered';
        const current = index === activeStep && status !== 'delivered';
        step.classList.toggle('is-complete', complete);
        step.classList.toggle('is-current', current);
        if (current) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    }

    renderNotice(payload, status) {
      let message = '';
      if (status === 'issue') {
        message = 'De vervoerder meldt een bezorgprobleem. Controleer de pakketupdate hieronder of neem contact met ons op.';
      } else if (status === 'delayed' || payload.delayed === true) {
        message = 'Deze bestelling duurt langer dan gebruikelijk. De meest recente pakketupdate staat hieronder.';
      } else if (status === 'available_for_pickup') {
        message = 'Je pakket ligt klaar bij een afhaalpunt. De meest recente pakketupdate staat hieronder.';
      }
      this.notice.textContent = message;
      this.notice.hidden = !message;
    }

    renderPackages(items) {
      this.packages.replaceChildren();
      this.packagesWrap.hidden = items.length === 0;
      if (items.length === 0) return;
      this.packagesHeading.textContent = items.length === 1 ? 'Pakket' : `${items.length} pakketten`;

      items.forEach((item, index) => {
        if (!isObject(item)) return;
        const status = typeof item.status === 'string' && STATUS_LABELS[item.status] ? item.status : 'unknown';
        const card = document.createElement('article');
        card.className = 'fot__package';

        const head = document.createElement('div');
        head.className = 'fot__package-head';
        head.append(textElement('h4', 'fot__package-title', item.label || `Pakket ${index + 1}`));
        const badge = textElement('span', 'fot__package-status', STATUS_LABELS[status]);
        badge.dataset.status = status;
        head.append(badge);
        card.append(head);

        const meta = [
          typeof item.carrier === 'string' ? item.carrier : null,
          typeof item.trackingSuffix === 'string' ? `tracking eindigt op ${item.trackingSuffix}` : null,
        ].filter(Boolean);
        if (meta.length > 0) card.append(textElement('p', 'fot__package-meta', meta.join(' · ')));

        const packageEta = formatEta(item.eta);
        if (packageEta && status !== 'delivered') {
          card.append(textElement('p', 'fot__package-eta', `Verwachte bezorging: ${packageEta} (geschat)`));
        }

        const deliveredAt = status === 'delivered' ? formatDeliveredDateTime(item.deliveredAt) : null;
        if (deliveredAt) {
          card.append(textElement('p', 'fot__package-eta', `Bezorgd op ${deliveredAt}`));
        }

        if (isObject(item.latestEvent)) {
          const eventParts = [];
          const eventTime = formatDateTime(item.latestEvent.at);
          if (eventTime) eventParts.push(eventTime);
          if (typeof item.latestEvent.description === 'string') eventParts.push(item.latestEvent.description);
          if (eventParts.length > 0) {
            card.append(textElement('p', 'fot__package-event', `Laatste update: ${eventParts.join(' · ')}`));
          }
        }

        this.packages.append(card);
      });
    }

    trackOutcome(outcome) {
      const detail = { outcome };
      this.dispatchEvent(new CustomEvent('finch:order-tracking-result', { bubbles: true, detail }));
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'order_tracking_result', outcome });
      }
    }
  }

  if (!customElements.get('finch-order-tracking')) {
    customElements.define('finch-order-tracking', FinchOrderTracking);
  }
})();
